// src/pages/secrets/ActiveConnectionsPage.tsx

import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  RefreshCw,
  Router,
  Search,
  ShieldCheck,
  Smartphone,
  Wifi,
  X,
  XCircle,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { API_CONFIG, fetchApp } from "../../services/apiService";

interface ActiveConnection {
  ".id"?: string;
  id?: string;
  name?: string;
  comment?: string;
  service?: string;
  caller_id?: string;
  "caller-id"?: string;
  address?: string;
  uptime?: string;
  encoding?: string;
  session_id?: string;
  "session-id"?: string;
  radius?: string | boolean;
  limit_bytes_in?: string | number;
  "limit-bytes-in"?: string | number;
  limit_bytes_out?: string | number;
  "limit-bytes-out"?: string | number;

  router_id?: string;
  router_name?: string;
  router_ip?: string;

  [key: string]: any;
}

interface ActiveRouterResult {
  router_id: string;
  router_name: string;
  router_ip?: string;
  ok: boolean;
  count: number;
  error?: string;
}

interface ActiveConnectionsResponse {
  ok: boolean;
  total: number;
  routers: ActiveRouterResult[];
  items: ActiveConnection[];
}

const getValue = (row: ActiveConnection, ...keys: string[]) => {
  for (const key of keys) {
    const value = row[key];

    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return String(value);
    }
  }

  return "—";
};

const isRadiusConnection = (row: ActiveConnection) => {
  const value = getValue(row, "radius").toLowerCase();
  return value === "true" || value === "yes";
};

const getCustomerDisplay = (row: ActiveConnection) => {
  const pppoeName = getValue(row, "name");
  const commentName = getValue(row, "comment");

  return {
    pppoeName,
    commentName,
    displayName: commentName !== "—" ? commentName : pppoeName,
  };
};

const inputCls =
  "h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 px-3.5 text-[13px] font-medium text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub: string;
  icon: React.ReactNode;
  tone: "slate" | "emerald" | "sky" | "amber";
}> = ({ label, value, sub, icon, tone }) => {
  const styles = {
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
      icon: "bg-slate-200 text-slate-700",
      text: "text-slate-600",
      bubble: "bg-slate-200/60",
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
      icon: "bg-emerald-100 text-emerald-700",
      text: "text-emerald-700",
      bubble: "bg-emerald-200/50",
    },
    sky: {
      card: "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 shadow-sky-200/50",
      icon: "bg-sky-100 text-sky-700",
      text: "text-sky-700",
      bubble: "bg-sky-200/50",
    },
    amber: {
      card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
      icon: "bg-amber-100 text-amber-700",
      text: "text-amber-700",
      bubble: "bg-amber-200/50",
    },
  }[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${styles.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${styles.bubble}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${styles.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${styles.text}`}
          >
            {label}
          </p>

          <p className="mt-0.5 truncate text-[22px] font-black tracking-tight text-slate-900 sm:text-3xl">
            {value}
          </p>

          <p className={`text-[11px] font-bold ${styles.text}`}>{sub}</p>
        </div>
      </div>
    </div>
  );
};

const RadiusBadge: React.FC<{ enabled: boolean; value?: string }> = ({
  enabled,
  value,
}) => {
  if (enabled) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-1 text-[11px] font-black text-emerald-800">
        <ShieldCheck className="h-3 w-3" />
        RADIUS
      </span>
    );
  }

  return (
    <span className="inline-flex rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
      {value || "No"}
    </span>
  );
};

const TableSkeleton: React.FC = () => (
  <tr className="animate-pulse">
    {Array.from({ length: 8 }).map((_, index) => (
      <td key={index} className="px-5 py-4">
        <div
          className={`h-4 rounded-lg bg-slate-200 ${
            index === 0
              ? "w-36"
              : index === 1
                ? "w-32"
                : index === 7
                  ? "w-20 rounded-full"
                  : "w-24"
          }`}
        />
      </td>
    ))}
  </tr>
);

const MobileSkeleton: React.FC = () => (
  <div className="animate-pulse px-4 py-4">
    <div className="flex items-start justify-between gap-3">
      <div className="space-y-2">
        <div className="h-4 w-36 rounded bg-slate-200" />
        <div className="h-3 w-24 rounded bg-slate-200" />
      </div>

      <div className="h-6 w-20 rounded-full bg-slate-200" />
    </div>

    <div className="mt-4 grid grid-cols-2 gap-2">
      <div className="h-14 rounded-2xl bg-slate-200" />
      <div className="h-14 rounded-2xl bg-slate-200" />
    </div>
  </div>
);

const ActiveConnectionsPage: React.FC = () => {
  const [connections, setConnections] = useState<ActiveConnection[]>([]);
  const [routers, setRouters] = useState<ActiveRouterResult[]>([]);

  const [search, setSearch] = useState("");
  const [routerFilter, setRouterFilter] = useState("all");

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadConnections = async () => {
    setRefreshing(true);
    setError(null);

    try {
      const res = (await fetchApp(
        `${API_CONFIG.baseUrl}/secrets/active/list`,
      )) as ActiveConnectionsResponse | ActiveConnection[];

      if (Array.isArray(res)) {
        setConnections(res);
        setRouters([]);
        return;
      }

      setConnections(Array.isArray(res?.items) ? res.items : []);
      setRouters(Array.isArray(res?.routers) ? res.routers : []);
    } catch (err: any) {
      setError(err?.message || "Failed to load active connections.");
      setConnections([]);
      setRouters([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadConnections();
  }, []);

  const routerOptions = useMemo(() => {
    const map = new Map<string, ActiveRouterResult>();

    routers.forEach((router) => {
      if (!router.router_id) return;
      map.set(router.router_id, router);
    });

    connections.forEach((connection) => {
      if (!connection.router_id) return;

      if (!map.has(connection.router_id)) {
        map.set(connection.router_id, {
          router_id: connection.router_id,
          router_name: connection.router_name || "Unknown Router",
          router_ip: connection.router_ip,
          ok: true,
          count: 0,
        });
      }
    });

    return Array.from(map.values()).sort((a, b) =>
      String(a.router_name || "").localeCompare(String(b.router_name || "")),
    );
  }, [routers, connections]);

  const filteredConnections = useMemo(() => {
    const q = search.trim().toLowerCase();

    return connections.filter((row) => {
      const matchesRouter =
        routerFilter === "all" || String(row.router_id) === routerFilter;

      if (!matchesRouter) return false;

      if (!q) return true;

      return [
        getValue(row, "comment"),
        getValue(row, "name"),
        getValue(row, "service"),
        getValue(row, "caller-id", "caller_id"),
        getValue(row, "address"),
        getValue(row, "uptime"),
        getValue(row, "session-id", "session_id"),
        getValue(row, "router_name"),
        getValue(row, "router_ip"),
      ]
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [connections, search, routerFilter]);

  const selectedRouter = useMemo(() => {
    if (routerFilter === "all") return null;
    return routerOptions.find((router) => router.router_id === routerFilter);
  }, [routerFilter, routerOptions]);

  const radiusCount = useMemo(() => {
    return filteredConnections.filter(isRadiusConnection).length;
  }, [filteredConnections]);

  const failedRouters = useMemo(() => {
    return routerOptions.filter((router) => !router.ok);
  }, [routerOptions]);

  const clearFilters = () => {
    setSearch("");
    setRouterFilter("all");
  };

  const hasFilters = search.trim() || routerFilter !== "all";

  if (loading) {
    return (
      <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
        <div className="mx-auto flex min-h-[55vh] max-w-[1600px] flex-col items-center justify-center gap-3 rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="flex h-14 w-14 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/40">
            <Loader2 className="h-7 w-7 animate-spin text-emerald-500" />
          </div>

          <p className="text-[13px] font-bold text-slate-500">
            Loading active PPP connections…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-5 sm:space-y-6">
        {/* Header */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10.5px] font-black uppercase tracking-[0.08em] text-emerald-700 sm:text-[11px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Live Router Sessions
                </div>

                <h1 className="mt-3 text-[22px] font-black tracking-[-0.5px] text-slate-900 sm:text-[24px]">
                  Active PPP Connections
                </h1>

                <p className="mt-1 max-w-2xl text-[13px] font-medium text-slate-500 sm:text-[13.5px]">
                  Monitor live PPPoE sessions currently active across your
                  MikroTik routers.
                </p>
              </div>

              <button
                type="button"
                onClick={loadConnections}
                disabled={refreshing}
                className={`${btnBase} h-10 bg-gradient-to-r from-emerald-500 to-teal-500 px-5 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
              >
                {refreshing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {refreshing ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="flex items-start justify-between gap-3 rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-[13px] font-bold text-rose-700 shadow-sm shadow-rose-200/40">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{error}</p>
            </div>

            <button
              type="button"
              onClick={() => setError(null)}
              className="rounded-xl p-1.5 text-rose-600 transition-colors hover:bg-rose-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {failedRouters.length > 0 && (
          <div className="rounded-3xl border border-amber-200 bg-amber-50 px-4 py-4 text-amber-800 shadow-sm shadow-amber-200/40 sm:px-5">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
                <AlertTriangle className="h-5 w-5" />
              </div>

              <div className="min-w-0">
                <p className="text-[13.5px] font-black">
                  Some routers could not be checked
                </p>

                <div className="mt-2 space-y-1">
                  {failedRouters.slice(0, 4).map((router) => (
                    <p
                      key={router.router_id}
                      className="text-[12px] font-medium text-amber-700"
                    >
                      <span className="font-black">{router.router_name}</span>:{" "}
                      {router.error || "Failed to connect"}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {refreshing && (
          <div className="flex items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] font-bold text-emerald-800 shadow-sm shadow-emerald-200/40">
            <Loader2 className="h-4 w-4 animate-spin" />
            Refreshing active sessions…
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 xl:gap-4">
          <StatCard
            label="Active Sessions"
            value={filteredConnections.length}
            sub="Current filtered sessions"
            icon={<Wifi className="h-5 w-5" />}
            tone="emerald"
          />

          <StatCard
            label="RADIUS Sessions"
            value={radiusCount}
            sub="Authenticated through RADIUS"
            icon={<ShieldCheck className="h-5 w-5" />}
            tone="sky"
          />

          <StatCard
            label="Routers Checked"
            value={routerOptions.length}
            sub="Routers included in check"
            icon={<Router className="h-5 w-5" />}
            tone="slate"
          />

          <StatCard
            label="Total Loaded"
            value={connections.length}
            sub="All active sessions loaded"
            icon={<Activity className="h-5 w-5" />}
            tone="amber"
          />
        </div>

        {/* Main Panel */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          {/* Toolbar */}
          <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                <Activity className="h-5 w-5" />
              </div>

              <div>
                <p className="text-[14px] font-black text-slate-900">
                  Active Session List
                </p>

                <p className="text-[11.5px] font-medium text-slate-500">
                  Showing {filteredConnections.length} of {connections.length}{" "}
                  active sessions
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
              <div className="relative w-full lg:w-80">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search customer, PPPoE, IP, MAC, router…"
                  className={`${inputCls} pl-10`}
                />
              </div>

              <div className="relative w-full lg:w-72">
                <Router className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                <select
                  value={routerFilter}
                  onChange={(e) => setRouterFilter(e.target.value)}
                  className={`${inputCls} cursor-pointer appearance-none pl-10 pr-9`}
                >
                  <option value="all">All Routers</option>
                  {routerOptions.map((router) => (
                    <option key={router.router_id} value={router.router_id}>
                      {router.router_name}
                      {/* {router.router_ip ? ` (${router.router_ip})` : ""} */}
                    </option>
                  ))}
                </select>

                <svg
                  className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>

              {hasFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  <X className="h-4 w-4" />
                  Clear
                </button>
              )}
            </div>
          </div>

          {selectedRouter && (
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 sm:px-5">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2 text-[13px]">
                  {selectedRouter.ok ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                  )}

                  <span className="font-black text-slate-800">
                    {selectedRouter.router_name}
                  </span>

                  <span className="font-mono text-[12px] text-slate-400">
                    {selectedRouter.router_ip || ""}
                  </span>
                </div>

                <p className="text-[12px] font-medium text-slate-500">
                  Router sessions:{" "}
                  <span className="font-black text-slate-800">
                    {selectedRouter.count}
                  </span>
                </p>
              </div>

              {!selectedRouter.ok && selectedRouter.error && (
                <p className="mt-1 text-[12px] font-bold text-amber-700">
                  {selectedRouter.error}
                </p>
              )}
            </div>
          )}

          {/* Desktop Table */}
          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full text-left text-[13px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100 text-[11px] uppercase tracking-widest text-slate-500">
                  <th className="px-5 py-3.5 font-black">Customer</th>
                  <th className="px-5 py-3.5 font-black">Router</th>
                  <th className="px-5 py-3.5 font-black">IP Address</th>
                  <th className="px-5 py-3.5 font-black">MAC / Caller ID</th>
                  <th className="px-5 py-3.5 font-black">Service</th>
                  <th className="px-5 py-3.5 font-black">Uptime</th>
                  <th className="px-5 py-3.5 font-black">Session</th>
                  <th className="px-5 py-3.5 font-black">RADIUS</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                {refreshing ? (
                  Array.from({ length: 6 }).map((_, index) => (
                    <TableSkeleton key={index} />
                  ))
                ) : filteredConnections.length > 0 ? (
                  filteredConnections.map((row, index) => {
                    const id = getValue(row, ".id", "id");
                    const { pppoeName, displayName } = getCustomerDisplay(row);

                    const address = getValue(row, "address");
                    const callerId = getValue(row, "caller-id", "caller_id");
                    const service = getValue(row, "service");
                    const uptime = getValue(row, "uptime");
                    const sessionId = getValue(row, "session-id", "session_id");
                    const radius = getValue(row, "radius");
                    const routerName = getValue(row, "router_name");
                    const routerIp = getValue(row, "router_ip");
                    const radiusEnabled = isRadiusConnection(row);

                    return (
                      <tr
                        key={`${id}-${row.router_id || "router"}-${index}`}
                        className="transition-colors hover:bg-emerald-50/60"
                      >
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
                              <Wifi className="h-4 w-4" />
                            </div>

                            <div className="min-w-0">
                              <p className="truncate text-[13px] font-black text-slate-900">
                                {displayName}
                              </p>

                              <p className="mt-0.5 font-mono text-[11px] font-medium text-slate-500">
                                PPPoE: {pppoeName}
                              </p>
                            </div>
                          </div>
                        </td>

                        <td className="px-5 py-4">
                          <div className="flex items-start gap-2">
                            <Router className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />

                            <div className="min-w-0">
                              <p className="truncate text-[12.5px] font-black text-slate-700">
                                {routerName}
                              </p>

                              <p className="font-mono text-[11px] font-medium text-slate-400">
                                {routerIp}
                              </p>
                            </div>
                          </div>
                        </td>

                        <td className="px-5 py-4">
                          <span className="rounded-xl border border-sky-200 bg-sky-100 px-2.5 py-1 font-mono text-[11.5px] font-black text-sky-800">
                            {address}
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          <div className="flex items-center gap-1.5 text-[12px] font-bold text-slate-600">
                            <Smartphone className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                            <span className="font-mono">{callerId}</span>
                          </div>
                        </td>

                        <td className="px-5 py-4">
                          <span className="rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
                            {service}
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          <div className="flex items-center gap-1.5 text-[12.5px] font-bold text-slate-600">
                            <Clock className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                            {uptime}
                          </div>
                        </td>

                        <td className="px-5 py-4">
                          <span className="font-mono text-[11.5px] font-medium text-slate-500">
                            {sessionId}
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          <RadiusBadge enabled={radiusEnabled} value={radius} />
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={8} className="px-5 py-16 text-center">
                      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                        <XCircle className="h-7 w-7 text-slate-500" />
                      </div>

                      <p className="text-[14px] font-black text-slate-800">
                        No active connections found
                      </p>

                      <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                        Active PPPoE users will appear here after they connect.
                      </p>

                      {hasFilters && (
                        <button
                          type="button"
                          onClick={clearFilters}
                          className="mt-4 text-[13px] font-black text-emerald-700 hover:text-emerald-800"
                        >
                          Clear filters
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="divide-y divide-slate-200 bg-slate-50 md:hidden">
            {refreshing ? (
              Array.from({ length: 5 }).map((_, index) => (
                <MobileSkeleton key={index} />
              ))
            ) : filteredConnections.length > 0 ? (
              filteredConnections.map((row, index) => {
                const id = getValue(row, ".id", "id");
                const { pppoeName, displayName } = getCustomerDisplay(row);

                const address = getValue(row, "address");
                const callerId = getValue(row, "caller-id", "caller_id");
                const service = getValue(row, "service");
                const uptime = getValue(row, "uptime");
                const sessionId = getValue(row, "session-id", "session_id");
                const radius = getValue(row, "radius");
                const routerName = getValue(row, "router_name");
                const routerIp = getValue(row, "router_ip");
                const radiusEnabled = isRadiusConnection(row);

                return (
                  <div
                    key={`${id}-${row.router_id || "router"}-${index}`}
                    className="px-4 py-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-[14px] font-black text-slate-900">
                          {displayName}
                        </p>

                        <p className="mt-0.5 font-mono text-[11.5px] font-medium text-slate-500">
                          PPPoE: {pppoeName}
                        </p>

                        <div className="mt-2 flex flex-wrap gap-2">
                          <span className="rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[10.5px] font-black text-slate-600">
                            {service}
                          </span>

                          <RadiusBadge enabled={radiusEnabled} value={radius} />
                        </div>
                      </div>

                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
                        <Wifi className="h-4 w-4" />
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <div className="rounded-2xl border border-sky-200 bg-sky-50 px-3 py-3">
                        <p className="text-[10px] font-black uppercase tracking-widest text-sky-700">
                          IP Address
                        </p>

                        <p className="mt-1 font-mono text-[12.5px] font-black text-slate-900">
                          {address}
                        </p>
                      </div>

                      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-3 py-3">
                        <p className="text-[10px] font-black uppercase tracking-widest text-amber-700">
                          Uptime
                        </p>

                        <p className="mt-1 text-[12.5px] font-black text-slate-900">
                          {uptime}
                        </p>
                      </div>
                    </div>

                    <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-100 px-3 py-3">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                        Router
                      </p>

                      <p className="mt-1 text-[12.5px] font-black text-slate-800">
                        {routerName}
                      </p>

                      <p className="mt-0.5 font-mono text-[11.5px] font-medium text-slate-500">
                        {routerIp}
                      </p>
                    </div>

                    <div className="mt-3 grid grid-cols-1 gap-2">
                      <div className="rounded-2xl border border-slate-200 bg-slate-100 px-3 py-3">
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                          MAC / Caller ID
                        </p>

                        <p className="mt-1 break-all font-mono text-[12px] font-bold text-slate-700">
                          {callerId}
                        </p>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-slate-100 px-3 py-3">
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                          Session ID
                        </p>

                        <p className="mt-1 break-all font-mono text-[12px] font-bold text-slate-700">
                          {sessionId}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="px-4 py-16 text-center">
                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                  <XCircle className="h-7 w-7 text-slate-500" />
                </div>

                <p className="text-[14px] font-black text-slate-800">
                  No active connections found
                </p>

                <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                  Active PPPoE users will appear here after they connect.
                </p>

                {hasFilters && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="mt-4 text-[13px] font-black text-emerald-700 hover:text-emerald-800"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex flex-col gap-2 border-t border-slate-200 bg-slate-100/80 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <p className="text-[12px] font-medium text-slate-500">
              Showing{" "}
              <span className="font-black text-slate-800">
                {filteredConnections.length}
              </span>{" "}
              of{" "}
              <span className="font-black text-slate-800">
                {connections.length}
              </span>{" "}
              active sessions
            </p>

            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-left text-[12px] font-black text-emerald-700 hover:text-emerald-800 sm:text-right"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Router Breakdown */}
        {routerOptions.length > 0 && (
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
            <div className="flex flex-col gap-2 border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-sky-100 text-sky-700">
                  <Router className="h-5 w-5" />
                </div>

                <div>
                  <p className="text-[14px] font-black text-slate-900">
                    Router Breakdown
                  </p>

                  <p className="text-[11.5px] font-medium text-slate-500">
                    Active sessions returned per router.
                  </p>
                </div>
              </div>

              {routerFilter !== "all" && (
                <button
                  type="button"
                  onClick={() => setRouterFilter("all")}
                  className="text-left text-[12px] font-black text-emerald-700 hover:text-emerald-800 sm:text-right"
                >
                  Show all routers
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 sm:p-5 xl:grid-cols-3">
              {routerOptions.map((router) => {
                const selected = routerFilter === router.router_id;

                return (
                  <button
                    key={router.router_id}
                    type="button"
                    onClick={() => setRouterFilter(router.router_id)}
                    className={`rounded-3xl border p-4 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 ${
                      selected
                        ? "border-emerald-300 bg-emerald-50 shadow-emerald-200/40"
                        : "border-slate-200 bg-slate-100 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-[13.5px] font-black text-slate-900">
                          {router.router_name}
                        </p>

                        <p className="mt-0.5 font-mono text-[11.5px] font-medium text-slate-500">
                          {router.router_ip || "No IP"}
                        </p>
                      </div>

                      {router.ok ? (
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                      ) : (
                        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
                      )}
                    </div>

                    <div className="mt-4 flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2">
                      <span className="text-[11px] font-black uppercase tracking-widest text-slate-500">
                        Active
                      </span>

                      <span className="text-[18px] font-black text-slate-900">
                        {router.count}
                      </span>
                    </div>

                    {!router.ok && router.error && (
                      <p className="mt-3 line-clamp-2 text-[12px] font-bold text-amber-700">
                        {router.error}
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActiveConnectionsPage;
